import React from 'react';
import gql from 'graphql-tag';

import { InputDlg } from './input-dialog';
import { Form as FForm, Formik, useField } from 'formik';
import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  FilterProps,
  promiseModalSelect,
  FormRow,
  TableCellV8,
  FFProps,
  FormFieldModalDlg,
  FormCol,
} from 'motion-components';
import { Form, Col } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import {
  CompanyVatTitle,
  isAfisCyprus,
  isFotokiklosi,
  isMedicycle,
  isRebattery,
  regionTitle,
} from 'shared-pages/common/constants';
import { FieldSelectNomos } from '../field-select/fs-nomos';
import { DialogOptionsContext, useDialogOptions } from './dialog-options-context';

export const FormFieldCollectionPointDlg = ({
  as = 'div',
  label = 'Σημείο Συλλογής',
  idFieldName = 'collectionPointId',
  nameFieldName = 'collectionPointName',
  disabled,
  placeholder = '',
  required = false,
  size,
  onChange,
  isParent, // NEW
}: FFProps & { isParent?: boolean }) => {
  const [, { value }] = useField(idFieldName);

  const path =
    value !== undefined && value !== null && value !== '' && value !== 'unknown'
      ? generatePath(getRouteByName('Collectionpoint'), { id: value })
      : '';
  const showLabel = value && path ? <Link to={path}>{label}</Link> : label;

  return (
    <DialogOptionsContext.Provider value={{ isParent }}>
      <FormFieldModalDlg
        DlgComponent={FieldCollectionPointDlg} // leave API unchanged
        as={as}
        label={showLabel}
        idFieldName={idFieldName}
        nameFieldName={nameFieldName}
        disabled={disabled}
        onChange={onChange}
        required={required}
      />
    </DialogOptionsContext.Provider>
  );
};

/* ------------------------------------------
   Select Collectionpoint with Modal Search
   ------------------------------------------ */
type Props = {
  initValueId?: string;
  initValueName?: string;
  onValueChange: (valueId: string, valueName: string) => void;
  required?: boolean;
  initFilters?: {} | (() => {});
  disabled?: boolean;
  isParent?: boolean; // NEW
};

export const FieldCollectionPointDlg = ({
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
  disabled = false,
  isParent: isParentProp,
}: Props) => {
  const { isParent: isParentCtx } = useDialogOptions();
  const isParent = isParentProp ?? isParentCtx;

  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const valueChanged = (valueId, valueName) => {
    // επιστρέφει valueId=uknown όταν πατηθεί το Cancel
    if (valueId) onValueChange(valueId, valueName);
  };

  return (
    <InputDlg
      valueId={initValueId}
      valueName={initValueName}
      disable={disabled}
      onDlgClick={async (event) => {
        const baseFilters = typeof initFilters === 'function' ? initFilters() : initFilters;
        const autoFetchData = !!baseFilters?.cstGuid;

        const res = await promiseModalSelectCollectionPoint({
          initFilters: { ...baseFilters, isParent }, // thread the flag
          appStore,
          autoFetchData,
          t,
          isParent, // explicit for builder closure
        });
        return { newId: res?.id, newName: res?.name };
      }}
      onValueChange={valueChanged}
    />
  );
};

// -------------------------------------------------------------------------------
// Modal Select Collectionpoint
// -------------------------------------------------------------------------------

const gqlGet = gql`
  query getCollectionpoints($where: String, $offset: Int, $limit: Int, $order: String) {
    getCollectionpoints(where: $where, offset: $offset, limit: $limit, order: $order) {
      data {
        id
        code
        name
        addressLine1
        addressCity
        addressArea
        addressDescription
        postalCode
        companyVat
        regionId
        region {
          id
          name
        }
        subregionId
        subregion {
          id
          name
        }
      }
      recordCount
    }
  }
`;

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values, appStore) => {
  // Φτιάξε το where για το graphql
  let where: any = { isActive: true };

  if (values) {
    if (values.searchText) {
      /*
      const searchText = '%' + values.searchText + '%';
      const likeObj = { $like: searchText };
      const orQuery =
        isRebattery && !appStore.isPlatformOwner
          ? [{ companyVat: likeObj }]
          : [{ name: likeObj }, { companyVat: likeObj }, { code: likeObj }];
      where = {
        ...where,
        $or: orQuery,
      };
      */
      const searchTerms = values.searchText
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term !== '');

      if (searchTerms.length > 0) {
        where = {
          ...where,
          $and: searchTerms.map((term) => {
            const likeObj = { $like: `%${term}%` };
            return {
              $or: [
                { code: likeObj },
                { name: likeObj },
                { companyVat: likeObj },
                { addressCity: likeObj },
                { addressArea: likeObj },
                { addressDescription: likeObj },
                { postalCode: likeObj },
              ],
            };
          }),
        };
      }
    }
    if (values.addressNomos) {
      where = { ...where, addressNomos: values.addressNomos };
    }

    // Parent-only constraint
    if (values.isParent === true) {
      where = { ...where, parentCpId: null };
    }
  }

  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  // console.log('$$$ [fd-collectionpoint] initialValues', initialValues);
  const { appStore } = useRootStore();
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-center">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label={
              isRebattery && !appStore.isPlatformOwner
                ? 'ΑΦΜ'
                : isAfisCyprus || isMedicycle
                ? 'Επωνυμία, Αρ. ΦΠΑ, Κωδικός'
                : 'Επωνυμία, ΑΦΜ, Κωδικός, Περιοχή, ΤΚ'
            }
            type="search"
            placeholder="Πολλοί όροι χωρισμένοι με κόμμα"
          />
          {isFotokiklosi && <FieldSelectNomos as={FormCol} />}
          <Col className="pl-0 pr-0 ">
            <ButtonSearch size="sm" type="submit" className="float-right" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------

export const promiseModalSelectCollectionPoint = (props) => {
  const t = props.t;
  const columns = [
    {
      id: 'code',
      header: 'Κωδικός',
      columnName: 'Κωδικός',
      accessorKey: 'code',
      orderByField: 'code',
      cell: TableCellV8,
      size: 120,
    },
    {
      id: 'name',
      header: t('Επωνυμία'),
      columnName: t('Επωνυμία'),
      accessorKey: 'name',
      orderByField: 'name',
      cell: TableCellV8,
      size: 120,
    },
    {
      id: 'companyVat',
      header: t(CompanyVatTitle),
      columnName: t(CompanyVatTitle),
      accessorKey: 'companyVat',
      orderByField: 'companyVat',
      cell: TableCellV8,
      size: 20,
    },
    {
      id: 'addressLine1',
      header: t('Διεύθυνση'),
      columnName: t('Διεύθυνση'),
      accessorKey: 'addressLine1',
      orderByField: 'addressLine1',
      cell: ({ getValue, row }) => (
        <div className="text-left">
          {row.original.addressLine1},&nbsp;
          {row.original.addressArea},&nbsp;
          {row.original.postalCode},&nbsp;
          {row.original.addressCity},&nbsp;
          {row.original.addressDistrict}
        </div>
      ),
      size: 40,
    },

    ...(isFotokiklosi
      ? [
          {
            id: 'addressCity',
            header: t('Πόλη'),
            columnName: t('Πόλη'),
            accessorKey: 'addressCity',
            orderByField: 'addressCity',
            cell: TableCellV8,
            size: 20,
          },
        ]
      : []),
    ...(isRebattery
      ? [
          {
            id: 'subregion.name',
            header: t('Περιφερειακή Ενότητα'),
            columnName: t('Περιφερειακή Ενότητα'),
            accessorKey: 'subregion.name',
            orderByField: 'subregion.name',
            cell: TableCellV8,
            size: 20,
          },
        ]
      : []),
    ...(isAfisCyprus || isMedicycle
      ? [
          {
            id: 'region.name',
            header: regionTitle,
            columnName: regionTitle,
            accessorKey: 'region.name',
            orderByField: 'region.name',
            cell: TableCellV8,
            size: 20,
          },
        ]
      : []),
  ];
  return promiseModalSelect({
    ...props,
    title: 'Επιλογή Σημείου Παραλαβής',
    columns,
    gqlQuery: gqlGet,
    buildWhereFromFilters: (values) =>
      buildWhereFromFilters({ ...values, isParent: props.isParent }, props.appStore),
    initFilters: props.initFilters,
    Filters: Filters,
    orderBy: 'name',
    size: 'xl',
  });
};
// -------------------------------------------------------------------------------
